import { Injectable, signal } from '@angular/core';
import pkce from '../auth/pkce';
import { LOCAL_STORAGE_KEY_ACCESS_TOKEN, LOCAL_STORAGE_KEY_USERNAME, LOCAL_STORAGE_KEY_EXPIRATION_TIME } from '../const';
import { logEvent } from '../app.config';
import { poe1 } from '../../game';

const ERROR_MSG = 'Something went wrong while trying to log in. Please contact us on Discord.';

@Injectable({
  providedIn: 'root'
})
export class OAuthService {
  status = signal<string | null>(null);
  userName = signal<string | null>(null);
  token = signal<string | null>(null);

  // Signal that emits when login succeeds
  loginSuccessEvent = signal<boolean>(false);

  constructor() {
    this.checkUrlParams();

    // Check whether the token is still valid and get username
    const accessToken = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN);
    const expirationTime = localStorage.getItem(LOCAL_STORAGE_KEY_EXPIRATION_TIME);
    const userName = localStorage.getItem(LOCAL_STORAGE_KEY_USERNAME);

    if (userName && expirationTime && Date.now() < (parseInt(expirationTime, 10) - 24 * 60 * 60 * 1000)) {
      // Token is still valid
      this.userName.set(userName);
      this.token.set(accessToken);
    } else {
      // Token is not valid
      localStorage.removeItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_KEY_USERNAME);
      localStorage.removeItem(LOCAL_STORAGE_KEY_EXPIRATION_TIME);
    }
  }

  async login() {
    logEvent('Login', 'Started');
    const code_verifier = pkce.generateVerifier();
    const code_challenge = await pkce.computeChallenge(code_verifier);
    const state = pkce.getRandomString(16);
    const scope = encodeURIComponent('account:profile account:item_filter');
    const redirectUri = encodeURIComponent(poe1 ? 'https://poe1filter.com/' : 'https://poe2filter.com/');
    const authUrl = `https://www.pathofexile.com/oauth/authorize?client_id=poe2filter&response_type=code&scope=${scope}&state=${state}&redirect_uri=${redirectUri}&code_challenge=${code_challenge}&code_challenge_method=S256`;

    sessionStorage.setItem('code_verifier', code_verifier);
    sessionStorage.setItem('state', state);

    window.location.href = authUrl;
  }

  logout() {
    localStorage.removeItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEY_USERNAME);
    localStorage.removeItem(LOCAL_STORAGE_KEY_EXPIRATION_TIME);

    this.userName.set(null);
    this.token.set(null);
  }

  checkUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('code') && urlParams.has('state')) {
      this.getToken(urlParams.get('code'), urlParams.get('state'));
    } else if (urlParams.has('error')) {
      this.errorAuthorizing(
        urlParams.get('error'),
        urlParams.get('error_description'),
        urlParams.get('error_uri')
      );
    }
    this.clearUrlParams();
  }

  clearUrlParams() {
    const url = new URL(window.location.href);
    url.search = '';
    window.history.replaceState({}, document.title, url.toString());
  }

  getToken(authorization_code: string | null, state: string | null) {
    // Verify state
    if (state !== sessionStorage.getItem('state')) {
      const errorMessage = ERROR_MSG + ' If you are using the Vivaldi browser with a pinned tab, this is a known error, try unpinning it and logging in again.';
      this.status.set(errorMessage);
      logEvent('Login', 'Invalid state', errorMessage);
      console.error(errorMessage);
      return;
    }

    this.status.set('Logging in...');

    // Exchange code for token
    const code_verifier = sessionStorage.getItem('code_verifier');

    fetch('/api/get-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        authorization_code,
        code_verifier
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error();
        }
      })
      .then(data => {
        // Store token and username in local storage
        // Calculate the expiration time and store it in local storage
        this.loginSuccess(data);
      })
      .catch(error => {
        const errorMessage = ('Error: ' + (error || 'Unspecified')).substring(0, 100);
        logEvent('Login', 'Error fetching access token', errorMessage);
        this.status.set(ERROR_MSG);
        console.error('Error fetching access token:', error);
      });
  }

  loginSuccess(data: any) {
    localStorage.setItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN, data.access_token);
    localStorage.setItem(LOCAL_STORAGE_KEY_USERNAME, data.username);
    localStorage.setItem(LOCAL_STORAGE_KEY_EXPIRATION_TIME, (Date.now() + data.expires_in * 1000).toString());

    this.userName.set(data.username);
    this.token.set(data.access_token);

    this.status.set(null);

    // Emit login success event
    this.loginSuccessEvent.set(true);
  }

  errorAuthorizing(error: string | null, errorDescription: string | null, errorUri: string | null) {
    const errorMessage = ('Error: ' + (error || 'Unspecified')).substring(0, 100);
    logEvent('Login', 'Error getting authorization token', errorMessage);
    this.status.set(ERROR_MSG);
    console.error('Error trying to get authorization token:', { error, errorDescription, errorUri });
  }
}
